// Radha — FX exposure and revaluation (070/071).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

export interface FxExposure {
  currency: string; invoice_count: number; outstanding_fc: string;
  rate_as_of: string | null; quote_date: string | null;
  outstanding_base: string; base_currency: string;
}

/** Open receivables per currency, converted at the rate on p_as_of. */
export function useFxExposure() {
  return useQuery({
    queryKey: ["fx-exposure"],
    queryFn: async (): Promise<FxExposure[]> => {
      const { data, error } = await supabase.rpc("fx_exposure");
      if (error) throw error;
      return (data as FxExposure[]) ?? [];
    },
    staleTime: 60_000,
  });
}

export interface FxRevaluation {
  id: string; as_of_date: string; base_currency: string;
  line_count: number; total_unrealised: string; created_at: string;
}

export function useFxRevaluations() {
  return useQuery({
    queryKey: ["fx-revaluations"],
    queryFn: async (): Promise<FxRevaluation[]> => {
      const { data, error } = await supabase
        .from("fx_revaluations")
        .select("id, as_of_date, base_currency, line_count, total_unrealised, created_at")
        .order("as_of_date", { ascending: false })
        .limit(12);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

/** Retranslate open foreign-currency receivables at a reporting date.
 *  Re-running the same date replaces its prior result rather than adding to
 *  it — a revaluation restates balances, it does not accumulate. */
export function useRunFxRevaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (asOf: string) => {
      const { data, error } = await supabase.rpc("run_fx_revaluation", { p_as_of: asOf });
      if (error) throw error;
      return (data as { line_count: number; total_unrealised: string; base_currency: string }[])?.[0];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fx-revaluations"] });
      qc.invalidateQueries({ queryKey: ["fx-exposure"] });
    },
  });
}

export function fxErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
